import type { EditionButtonReturn } from '@ingenico/react-section-utils';
import type { KebabMenuOption } from '@ingenico/react-ui/kebab-menu';
import type { GridCallbackDetails, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { TFunction } from 'i18next';
import type { MutableRefObject, ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { OverViewPayloadDTO } from '../../components/overview-tab/Overviewtab.model';
import type { ApplicationResource } from '../../domain/models/application.model';
import type { MappedApplicationVersionResourceListResponseV2Api } from '../../domain/models/applicationVersion.model';
import type { VersionQuickFilter } from '../../hooks/useVersionQuickFilter.hook';

export enum ApplicationDetailsTabs {
  OVERVIEW = 'OVERVIEW',
  VERSION = 'VERSION',
  DELIVERY = 'DELIVERY',
  ORGANIZATIONS = 'ORGANIZATIONS',
  SHARING = 'SHARING',
}

export interface OsVersionRow {
  readonly deviceFamilyVersion: string;
  readonly version: string;
}

export enum VersionStatus {
  PUBLISHED = 'published',
  DEPRECATED = 'deprecated',
}

export interface VersionRows {
  readonly version: string;
  readonly creationDate: Date | string;
  readonly size?: string;
  readonly status: VersionStatus;
  readonly id: string;
}

export interface OSVersion {
  readonly name: string;
  readonly version: string;
}

export interface ApplicationTabColumns {
  readonly columns: GridColDef[];
  readonly defaultAvailableColumns: string[];
}

export interface Status {
  readonly isFetching: boolean;
  readonly isError: boolean;
  readonly isSuccess: boolean;
}

export interface CrudPermissions {
  readonly isList: boolean;
  readonly isRead: boolean;
  readonly isCreate: boolean;
  readonly isUpdate: boolean;
}
export interface ApplicationDetailsPageVMOutput extends ApplicationTabColumns {
  readonly details: Partial<OverViewPayloadDTO>;
  readonly deprecateOrRestoreConfirmationComponentChildren: ReactNode;
  readonly kebabValues: KebabMenuOption[];
  readonly editButton: EditionButtonReturn;
  readonly form: UseFormReturn<ApplicationResource>;
  readonly handleAddNewVersionClick: () => void;
  readonly isOverviewEditable: boolean;
  readonly overviewStatus: Status;
  readonly publisherName?: string;
  readonly shouldShowOrganizationsTab: boolean;
  readonly shouldShowSharingTab: boolean;
  readonly versionTabPermissions: Pick<CrudPermissions, 'isCreate' | 'isList'>;
  readonly response: AppVersionsListResponse;
  readonly search: SearchData;
  readonly quickFiltersSearch: VersionQuickFilter;
  readonly gridProps: AppVersionsGridProps;
  readonly shouldDisplayGrid: boolean;
}

export interface IUseApplicationDetailsPageVMProps {
  readonly t: TFunction;
}

export interface AppVersionsListResponse {
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly data: MappedApplicationVersionResourceListResponseV2Api;
  readonly error?: unknown;
  readonly isSuccess?: boolean;
  readonly isError?: boolean;
}

export interface SearchData {
  readonly handleSearchQueryChange: (v: string) => void;
  readonly searchQuery: string;
}

export interface AppVersionsGridProps {
  readonly ref: MutableRefObject<GridApiPremium>;
  readonly total: number;
  readonly handlePaginationModelChange: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>,
  ) => void;
  readonly handleSortModelChange: (sort: GridSortModel, details: GridCallbackDetails) => void;
}
export interface UseGetOsVersionsForApplicationsDetailsResponse {
  readonly response: AppVersionsListResponse;
  readonly search: SearchData;
  readonly gridProps: AppVersionsGridProps;
  readonly shouldDisplayGrid: boolean;
}
